import clsx from 'clsx';
import { addMinutes, format } from 'date-fns';
import { Check, Copy } from 'lucide-react';
import { type FC, useState } from 'react';
import { useUser } from '../../hooks';
import { useOpenMeetingLink } from '../../hooks/useOpenMeetingLink';
import { getMeetingUrl } from '../../utils/environment';
import { copyInvitationText } from '../../utils/meetingInvitation';
import { isPlainLeftClick } from '../../utils/meetingLinkRouting';
import { formatPin } from '../ScheduleMeetingModal/hooks/useDialInData';

interface MeetingCardData {
  room: string;
  description?: string | null;
  password?: string | null;
  scheduledTime?: string;
  duration?: number;
}

interface Props {
  meetingCard?: MeetingCardData;
  onClose: () => void;
  phoneNumber?: string | null;
  pin?: string | null;
  hasLicenseError?: boolean;
  isOtherCalendar?: boolean;
}

const MeetingInvitationCard: FC<Props> = ({
  meetingCard,
  onClose,
  phoneNumber,
  pin,
  hasLicenseError = false,
  isOtherCalendar = false,
}) => {
  const [copied, setCopied] = useState(false);
  const { userInfo } = useUser();
  const openMeetingLink = useOpenMeetingLink();
  const passcode = meetingCard?.password?.trim();
  const meetingLink = getMeetingUrl(meetingCard?.room || '');

  const dialIn = hasLicenseError
    ? { hasLicenseError: true }
    : phoneNumber != null || pin != null
      ? { phoneNumber: phoneNumber ?? null, pin: pin ?? null }
      : undefined;

  async function copyInvitation() {
    try {
      await navigator.clipboard.writeText(
        copyInvitationText(meetingCard?.room || '', dialIn, passcode || ''),
      );
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('copy failed', err);
    }
  }

  function formatMeetingDate(isoDate: string, duration: number = 30) {
    const start = new Date(isoDate);
    const end = addMinutes(start, duration);

    return `${format(start, 'EEEE MMMM d')} at ${format(start, 'h:mm')} - ${format(end, 'h:mm a')}`;
  }

  function renderDialInSection() {
    if (hasLicenseError) {
      return null; // This shouldn't be called if hasLicenseError is true, but defensive check
    }

    if (phoneNumber == null && pin == null) {
      return (
        <p className="meeting-invitation-card__text">
          Dial-in information is not available for this meeting.
        </p>
      );
    }

    return (
      <p className="meeting-invitation-card__text">
        {phoneNumber != null && (
          <>
            Dial:{' '}
            <a
              href={`tel:${phoneNumber}${pin ? `,${formatPin(pin)}` : ''}`}
              className="meeting-invitation-card__link"
            >
              {phoneNumber}
            </a>
          </>
        )}
        {phoneNumber != null && pin != null && ' | '}
        {pin != null && (
          <>
            PIN: <span className="meeting-invitation-card__link">{formatPin(pin)}</span>
          </>
        )}
      </p>
    );
  }

  return (
    <div className="schedule-meeting-modal-overlay">
      <div className="schedule-meeting-modal">
        {/* Header */}
        <div
          className={clsx('schedule-meeting-modal__header', {
            'schedule-meeting-modal__header--with-subtitle': isOtherCalendar,
          })}
        >
          <div>
            <div className="schedule-meeting-modal__title">
              {isOtherCalendar ? 'Meeting has been created!' : 'Meeting has been scheduled!'}
            </div>
            {isOtherCalendar && (
              <div className="schedule-meeting-modal__subtitle">
                Please copy invitation details below to forward to recipients
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="schedule-meeting-modal__close-button">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              className="schedule-meeting-modal__close-icon"
            >
              <title>Close</title>
              <path d="M15.25 4.75833C14.925 4.43333 14.4 4.43333 14.075 4.75833L9.99998 8.825L5.92498 4.75C5.59998 4.425 5.07498 4.425 4.74998 4.75C4.42498 5.075 4.42498 5.6 4.74998 5.925L8.82498 10L4.74998 14.075C4.42498 14.4 4.42498 14.925 4.74998 15.25C5.07498 15.575 5.59998 15.575 5.92498 15.25L9.99998 11.175L14.075 15.25C14.4 15.575 14.925 15.575 15.25 15.25C15.575 14.925 15.575 14.4 15.25 14.075L11.175 10L15.25 5.925C15.5666 5.60833 15.5666 5.075 15.25 4.75833Z" />
            </svg>
          </button>
        </div>

        <div className="schedule-meeting-modal__divider" />

        {/* Body */}
        <div className="schedule-meeting-modal__content">
          <div className="schedule-meeting-modal__body">
            <div className="meeting-invitation-card">
              <div className="meeting-invitation-card__box">
                <h4 className="font-medium mb-2 meeting-invitation-card__title">
                  Meeting invitation
                </h4>
                <p className="meeting-invitation-card__text">
                  {userInfo?.given_name} is inviting you to a scheduled Meet meeting.
                </p>
              </div>

              <div className="meeting-invitation-card__box">
                {meetingCard?.description && (
                  <p className="meeting-invitation-card__text">
                    Topic: <span className="font-normal">{meetingCard?.description}</span>
                  </p>
                )}
                {meetingCard?.scheduledTime && (
                  <p className="meeting-invitation-card__text">
                    Date and time:{' '}
                    <span className="font-normal">
                      {formatMeetingDate(meetingCard.scheduledTime, meetingCard.duration || 60)}
                    </span>
                  </p>
                )}
              </div>

              <div className="meeting-invitation-card__box">
                <p className="meeting-invitation-card__text">
                  Join this meeting from computer or mobile device:
                </p>
                <a
                  href={meetingLink}
                  className="meeting-invitation-card__link"
                  onClick={(event) => {
                    if (!isPlainLeftClick(event)) {
                      return;
                    }
                    event.preventDefault();
                    openMeetingLink(meetingLink);
                  }}
                >
                  {meetingLink}
                </a>
              </div>

              {!hasLicenseError && (
                <div className="meeting-invitation-card__box">
                  <p className="meeting-invitation-card__text">Want to join using a phone?</p>
                  {renderDialInSection()}
                </div>
              )}

              <div className="meeting-invitation-card__box">
                <p className="meeting-invitation-card__text">Meeting ID: {meetingCard?.room}</p>
                {passcode && <p className="meeting-invitation-card__text">Passcode: {passcode}</p>}
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="schedule-meeting-modal__footer">
            <button
              type="button"
              className="meeting-invitation-card__copy-button"
              onClick={copyInvitation}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              <span>{copied ? 'Copied' : 'Copy invitation'}</span>
            </button>

            <button
              className="schedule-meeting-modal__button schedule-meeting-modal__button--primary"
              type="button"
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingInvitationCard;
